/**
 * L'APERCU DU DOSSIER, la logique sans ecran (testee par `apercu.test.mjs`).
 * Decisions du fondateur le 26/10/2026 : le veterinaire lit l'apercu dans le
 * couloir, du plus urgent au moins urgent. Les alertes et les notes internes
 * sont deja dans la banniere rouge, au-dessus du parcours, sur tous les onglets
 * (zone 1) ; l'apercu porte les trois autres zones :
 *   2. pourquoi il vient (le motif du jour, ce que la derniere visite demandait) ;
 *   3. a faire ou proposer aujourd'hui (rappels, taches ouvertes) ;
 *   4. son historique (les dernieres visites, leurs anomalies d'examen et leur
 *      traitement, la courbe du poids).
 * Les traitements au long cours ne sont pas suivis (sauf hospitalisation) : ils
 * se lisent dans l'historique, par la therapie de chaque visite.
 */

import type { PointExamine } from './examen.js';

/** Une visite passee, telle que l'apercu la lit. */
export interface VisitePassee {
  id: string;
  quand: Date;
  motif: string;
  diagnostic: string;
  therapie: string;
  suivi: string;
  veterinaire: string;
}

export interface LigneDHistorique {
  id: string;
  quand: Date;
  motif: string;
  diagnostic: string;
  /** Les points anormaux de l'examen en liste : « oreilles gauche : cérumen ++ ». */
  anomalies: string[];
  /** Vrai : un examen en liste existe et n'a rien d'anormal. */
  examenNormal: boolean;
  therapie: string;
  veterinaire: string;
}

/** Les libelles des systemes, pour nommer un point anormal. */
export type LibellesDesSystemes = ReadonlyMap<string, string>;

/** Un point anormal en mots : « Oreilles gauche : cérumen ++, prurit ». */
export function anomalieEnMots(point: PointExamine, systemes: LibellesDesSystemes): string {
  const systeme = systemes.get(point.systeme) ?? '';
  const nom = systeme === '' ? point.libelle : point.libelle === systeme ? systeme : `${systeme} ${point.libelle.toLowerCase()}`;
  const detail = [...point.choix, ...(point.note.trim() === '' ? [] : [point.note.trim()])];
  return detail.length === 0 ? nom : `${nom} : ${detail.join(', ').toLowerCase()}`;
}

/**
 * L'HISTORIQUE : les `limite` dernieres visites avant le jour de la
 * consultation, la plus recente d'abord, chacune avec les anomalies de son
 * examen en liste s'il y en a un.
 */
export function lignesDHistorique(
  passees: readonly VisitePassee[],
  examens: ReadonlyMap<string, readonly PointExamine[]>,
  systemes: LibellesDesSystemes,
  avant: Date,
  limite = 3,
): LigneDHistorique[] {
  return passees
    .filter((v) => v.quand.getTime() < avant.getTime())
    .sort((a, b) => b.quand.getTime() - a.quand.getTime())
    .slice(0, limite)
    .map((v) => {
      const points = examens.get(v.id) ?? [];
      const anomalies = points.filter((p) => p.etat === 'anormal').map((p) => anomalieEnMots(p, systemes));
      return {
        id: v.id,
        quand: v.quand,
        motif: v.motif,
        diagnostic: v.diagnostic,
        anomalies,
        examenNormal: points.some((p) => p.etat !== 'nonExamine') && anomalies.length === 0,
        therapie: v.therapie,
        veterinaire: v.veterinaire,
      };
    });
}

/** Les suivis qui demandent quelque chose a la visite suivante. */
const SUIVIS_A_REPRENDRE = ['Contrôle suivant', 'Contrôle dans une semaine', 'Contrôle dans un mois', 'Résultats à communiquer', 'Hospitalisation'];

/**
 * CE QUE LA DERNIERE VISITE DEMANDAIT : son suivi s'il appelle une suite
 * (contrôle, résultats, hospitalisation), avec la date et le diagnostic, ou null.
 */
export function demandeDeLaDerniereVisite(passees: readonly VisitePassee[], avant: Date): { suivi: string; quand: Date; diagnostic: string } | null {
  const derniere = [...passees].filter((v) => v.quand.getTime() < avant.getTime()).sort((a, b) => b.quand.getTime() - a.quand.getTime())[0];
  if (derniere === undefined || !SUIVIS_A_REPRENDRE.includes(derniere.suivi)) return null;
  /* Le diagnostic va entre parentheses : son point final tombe. */
  return { suivi: derniere.suivi, quand: derniere.quand, diagnostic: derniere.diagnostic.trim().replace(/\.$/, '') };
}

/** Un point de la courbe du poids. */
export interface PointDePoids {
  quand: Date;
  kg: number;
}

/**
 * LA COURBE DU POIDS : les pesees de l'examen (une `measurement` `weight` par
 * visite), dans l'ordre du temps. Sans aucune pesee, le poids de la fiche
 * animal fait un point seul, date du jour (decision du fondateur le 26/10 :
 * on l'affiche quand meme).
 */
export function courbeDuPoids(pesees: readonly PointDePoids[], poidsDeLaFiche: unknown, aujourdhui: Date): PointDePoids[] {
  const triees = pesees.filter((p) => Number.isFinite(p.kg) && p.kg > 0).sort((a, b) => a.quand.getTime() - b.quand.getTime());
  if (triees.length > 0) return triees;
  return typeof poidsDeLaFiche === 'number' && poidsDeLaFiche > 0 ? [{ quand: aujourdhui, kg: poidsDeLaFiche }] : [];
}

/** L'ecart de poids sur l'annee : la derniere pesee moins la plus ancienne des douze derniers mois. Null sous deux pesees. */
export function ecartSurUnAn(courbe: readonly PointDePoids[]): number | null {
  const derniere = courbe[courbe.length - 1];
  if (derniere === undefined) return null;
  const ilYAUnAn = derniere.quand.getTime() - 365 * 24 * 60 * 60 * 1000;
  const premiere = courbe.find((p) => p.quand.getTime() >= ilYAUnAn);
  if (premiere === undefined || premiere === derniere) return null;
  return Math.round((derniere.kg - premiere.kg) * 10) / 10;
}
